/**
 * Creative preview — renders an admin-facing thumbnail for a funnel creative.
 *   • kind = image          → <img> with media_url (or placeholder card)
 *   • kind = short/long_anim → cycle through `animation_spec.hooks` scenes on
 *                              a Fabric StaticCanvas (opacity crossfade)
 *
 * Small, self-contained, no Fabric outside this file in admin land.
 */
import { useEffect, useRef, useState } from "react";
import { Film, Image as ImageIcon, Sparkles } from "lucide-react";

const W = 260;
const H = 160;

export default function FunnelCreativePreview({ creative }) {
  if (!creative) return null;
  if (creative.kind === "image") return <ImagePreview creative={creative} />;
  return <AnimPreview creative={creative} />;
}

// ---- Image ---------------------------------------------------------------

function ImagePreview({ creative }) {
  const { media_url, headline, cta } = creative;
  // Relative URLs (from Gemini gen) need the backend origin prepended; absolute URLs pass through.
  const resolvedUrl = media_url && media_url.startsWith("/")
    ? `${process.env.REACT_APP_BACKEND_URL}${media_url}`
    : media_url;
  if (resolvedUrl) {
    return (
      <div style={{ position: "relative", borderRadius: 8, overflow: "hidden", background: "#0a0a0a", height: H }}>
        <img
          src={resolvedUrl}
          alt={headline}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          data-testid="creative-preview-img"
        />
        <div style={overlayStyle}>
          <p style={headlineStyle}>{headline}</p>
          {cta && <span style={ctaStyle}>{cta}</span>}
        </div>
      </div>
    );
  }
  // No image yet — render a gold/black branded placeholder card.
  return (
    <div style={placeholderStyle} data-testid="creative-preview-img-placeholder">
      <ImageIcon size={32} color="#D4AF3788" />
      <p style={{ color: "#D4AF37", fontWeight: 900, fontSize: 13, margin: "10px 0 6px", textAlign: "center", lineHeight: 1.2 }}>
        {headline || "Bez headline"}
      </p>
      {cta && <span style={miniCtaStyle}>{cta}</span>}
    </div>
  );
}

// ---- Short / Long animation (Fabric StaticCanvas) ------------------------

function AnimPreview({ creative }) {
  const containerRef = useRef(null);
  const canvasARef = useRef(null);
  const canvasBRef = useRef(null);
  const fabA = useRef(null);
  const fabB = useRef(null);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [ready, setReady] = useState(false);

  const spec = creative.animation_spec || {};
  const hooks = spec.hooks && spec.hooks.length
    ? spec.hooks
    : [creative.headline || "…", creative.primary_text?.slice(0, 60) || "", creative.cta || ""].filter(Boolean);
  const colors = spec.colors || { primary: "#D4AF37", bg: "#0a0a0a", fg: "#ffffff" };
  const durationPerScene = Math.max(800, Math.min(3000, spec.duration_ms_per_scene || 2000));

  // Load Fabric lazily (already installed elsewhere).
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const fabricMod = await import("fabric");
        const fabric = fabricMod.default || fabricMod;
        if (!mounted || !canvasARef.current || !canvasBRef.current) return;
        fabA.current = new fabric.StaticCanvas(canvasARef.current, { width: W, height: H, backgroundColor: colors.bg });
        fabB.current = new fabric.StaticCanvas(canvasBRef.current, { width: W, height: H, backgroundColor: colors.bg });
        renderScene(fabric, fabA.current, hooks[0], colors);
        setReady(true);
      } catch (e) {
        // Fabric not available — fall through to text-only preview.
      }
    })();
    return () => { mounted = false; try { fabA.current?.dispose(); fabB.current?.dispose(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creative.id]);

  // Scene cycling — crossfade between A and B.
  useEffect(() => {
    if (!ready) return;
    const t = setInterval(() => setSceneIdx((i) => (i + 1) % hooks.length), durationPerScene);
    return () => clearInterval(t);
  }, [ready, durationPerScene, hooks.length]);

  useEffect(() => {
    if (!ready) return;
    (async () => {
      const fabricMod = await import("fabric");
      const fabric = fabricMod.default || fabricMod;
      const target = sceneIdx % 2 === 0 ? fabA.current : fabB.current;
      renderScene(fabric, target, hooks[sceneIdx], colors);
    })();
  }, [sceneIdx, ready, hooks, colors]);

  const aVisible = sceneIdx % 2 === 0;

  return (
    <div ref={containerRef} style={{ position: "relative", width: W, height: H, borderRadius: 8, overflow: "hidden" }}
         data-testid="creative-preview-anim">
      <canvas ref={canvasARef} style={{ position: "absolute", inset: 0, opacity: aVisible ? 1 : 0, transition: "opacity 280ms" }} />
      <canvas ref={canvasBRef} style={{ position: "absolute", inset: 0, opacity: aVisible ? 0 : 1, transition: "opacity 280ms" }} />
      {/* Scene dots */}
      <div style={{ position: "absolute", bottom: 6, left: 0, right: 0, display: "flex", gap: 4, justifyContent: "center" }}>
        {hooks.map((_, i) => (
          <span key={i} style={{
            width: 5, height: 5, borderRadius: "50%",
            background: i === sceneIdx ? colors.primary : "#ffffff33",
            transition: "background 180ms",
          }} />
        ))}
      </div>
      {/* Kind badge */}
      <div style={{ position: "absolute", top: 6, left: 6, background: "#00000099", color: colors.primary,
                    fontSize: 9, fontWeight: 900, letterSpacing: 1, padding: "2px 6px", borderRadius: 4,
                    display: "flex", alignItems: "center", gap: 3 }}>
        <Film size={10} />
        {creative.kind === "short_anim" ? "SHORT" : "LONG"}
      </div>
    </div>
  );
}

function renderScene(fabric, canvas, text, colors) {
  if (!canvas) return;
  canvas.clear();
  canvas.backgroundColor = colors.bg;
  // Subtle radial glow via ellipse with low opacity
  const glow = new fabric.Ellipse({
    left: W * 0.15, top: H * 0.2, rx: W * 0.4, ry: H * 0.4,
    fill: colors.primary, opacity: 0.12, originX: "left", originY: "top",
  });
  canvas.add(glow);
  // Accent bar
  const bar = new fabric.Rect({
    left: 10, top: 10, width: 28, height: 3, fill: colors.primary,
    originX: "left", originY: "top",
  });
  canvas.add(bar);
  // Main text
  const displayText = String(text || "").slice(0, 70);
  const tb = new fabric.Textbox(displayText, {
    left: 16, top: 30, width: W - 32, originX: "left", originY: "top",
    fontSize: displayText.length > 30 ? 14 : 18,
    fontWeight: 900, fontFamily: "Arial, sans-serif",
    fill: colors.fg, lineHeight: 1.2,
  });
  canvas.add(tb);
  canvas.requestRenderAll();
}

// ---- Styles --------------------------------------------------------------

const overlayStyle = {
  position: "absolute", left: 0, right: 0, bottom: 0,
  padding: "12px 10px 10px",
  background: "linear-gradient(to top, rgba(0,0,0,0.92), transparent)",
  display: "flex", flexDirection: "column", gap: 6,
};
const headlineStyle = { margin: 0, color: "#fff", fontSize: 12, fontWeight: 900, lineHeight: 1.25, textShadow: "0 1px 4px rgba(0,0,0,0.8)" };
const ctaStyle = { alignSelf: "flex-start", padding: "2px 8px", background: "#D4AF37", color: "#0a0a0a", fontSize: 10, fontWeight: 900, borderRadius: 3, textTransform: "uppercase", letterSpacing: 0.5 };
const miniCtaStyle = { padding: "2px 8px", background: "#D4AF37", color: "#0a0a0a", fontSize: 10, fontWeight: 900, borderRadius: 3, textTransform: "uppercase", letterSpacing: 0.5 };
const placeholderStyle = {
  width: W, height: H, borderRadius: 8,
  background: "linear-gradient(135deg, #1a1307, #0a0a0a)",
  border: "1px dashed #D4AF3755",
  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 12,
};
